import { ArrowDownwardRounded, DeleteRounded, EditRounded } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  styled,
  useTheme,
} from '@mui/material';
import { DEFAULT_VIRTUAL_FENCE_COLOR, FENCE_RISK_COLORS, RiskClassification } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import DoNotShowInProduction from 'app/shared/components/do-not-show-in-production/do-not-show-in-production.component';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { IVirtualFenceSummary, VirtualFenceType } from 'app/shared/model/virtual-fence.model';
import { VirtualFenceSyncStatus } from 'app/shared/model/virtual-fence-circle.model';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC, SORT } from 'app/shared/util/pagination.constants';
import { VirtualFenceTypeFilter } from 'app/entities/virtual-fence/virtual-fence-list.reducer';
import { getEntities, pollFences } from 'app/entities/virtual-fence/virtual-fence-list.reducer';
import { VirtualFenceCreateChooser } from './virtual-fence-create-chooser';
import { getSortState } from 'react-jhipster';
import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

const POLL_INTERVAL_MS = 10000;

const isPending = (syncStatus?: VirtualFenceSyncStatus | null): boolean =>
  syncStatus === 'PENDING_SYNC' || syncStatus === 'PENDING_DELETE';

const isDeleteBlocked = (syncStatus?: VirtualFenceSyncStatus | null): boolean =>
  syncStatus === 'PENDING_DELETE';

// ── Helpers ───────────────────────────────────────────────────────────────────

const TypeChip = ({ type }: { type?: VirtualFenceType | null }) => {
  const isPolygon = type === 'POLYGON';
  return (
    <Chip
      label={isPolygon ? 'Poligonal' : 'Circular'}
      size="small"
      variant="outlined"
      sx={{ fontSize: '0.7rem', height: 20, borderRadius: '4px' }}
    />
  );
};

const GeometryCell = ({ fence }: { fence: IVirtualFenceSummary }) => {
  if (fence.type === 'POLYGON') {
    return (
      <Typography color="primary.contrastText" fontSize="0.875rem">
        {fence.pointCount != null ? `${fence.pointCount} pontos` : '-'}
      </Typography>
    );
  }
  return (
    <Typography color="primary.contrastText" fontSize="0.875rem">
      {fence.radiusMeters != null ? `${fence.radiusMeters} m` : '-'}
    </Typography>
  );
};

const RISK_LABEL: Record<RiskClassification, string> = {
  SAFE: 'Segura',
  UNSAFE: 'Insegura',
  CONTROL: 'Controle',
};

const ClassificationChip = ({ fence }: { fence: IVirtualFenceSummary }) => {
  const classification = fence.riskClassification;
  const color = classification ? FENCE_RISK_COLORS[classification] : (fence.color ?? DEFAULT_VIRTUAL_FENCE_COLOR);
  const label = classification ? RISK_LABEL[classification] : null;

  return label ? (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        fontSize: '0.7rem',
        height: 20,
        borderRadius: '4px',
        borderColor: color,
        color,
      }}
    />
  ) : (
    <Typography color="text.secondary" fontSize="0.8rem">-</Typography>
  );
};

const StatusChip = ({ fence }: { fence: IVirtualFenceSummary }) => {
  const syncStatus = fence.syncStatus;
  const count = `(${fence.syncedDeviceCount ?? 0}/${fence.totalDeviceCount ?? 0})`;
  if (syncStatus === 'PENDING_DELETE') {
    return (
      <Chip
        label={`Pendente de exclusão ${count}`}
        size="small"
        variant="outlined"
        color="error"
        sx={{ fontSize: '0.7rem', height: 22, borderRadius: '4px' }}
      />
    );
  }

  if (syncStatus === 'PENDING_SYNC') {
    return (
      <Chip
        label={`Pendente ${count}`}
        size="small"
        variant="outlined"
        color="warning"
        sx={{ fontSize: '0.7rem', height: 22, borderRadius: '4px' }}
      />
    );
  }

  if (syncStatus === 'SYNCED') {
    return (
      <Chip
        label={`Sincronizada ${count}`}
        size="small"
        variant="outlined"
        color="success"
        sx={{ fontSize: '0.7rem', height: 22, borderRadius: '4px' }}
      />
    );
  }

  return (
    <Typography color="text.secondary" fontSize="0.8rem">
      -
    </Typography>
  );
};

export const VirtualFenceList = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [typeFilter, setTypeFilter] = useState<VirtualFenceTypeFilter>('ALL');
  const [chooserOpen, setChooserOpen] = useState(false);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'name'), props.location.search)
  );

  const fenceList    = useAppSelector(state => state.virtualFenceList.entities);
  const loading      = useAppSelector(state => state.virtualFenceList.loading);
  const totalItems   = useAppSelector(state => state.virtualFenceList.totalItems);
  const circleCount  = useAppSelector(state => state.virtualFenceList.circleCount);
  const polygonCount = useAppSelector(state => state.virtualFenceList.polygonCount);

  const fetchEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        type: typeFilter,
      })
    );
  };

  const sortEntities = () => {
    fetchEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, typeFilter]);

  useEffect(() => {
    const params = new URLSearchParams(props.location.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState(prev => ({
        ...prev,
        activePage: +page,
        sort: sortSplit[0] || 'id',
        order: sortSplit[1] || 'asc',
      }));
    }
  }, [props.location.search]);

  useEffect(() => {
    const hasPendingItems = fenceList.some(f => isPending(f.syncStatus));

    if (hasPendingItems) {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(() => {
          dispatch(
            pollFences({
              page: paginationState.activePage - 1,
              size: paginationState.itemsPerPage,
              sort: `${paginationState.sort},${paginationState.order}`,
              type: typeFilter,
            })
          );
        }, POLL_INTERVAL_MS);
      }
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [fenceList]);

  const pageQueryStr = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;

  const goToEdit = (fence: IVirtualFenceSummary) => {
    if (!fence.id) return;
    const editQueryStr = `${pageQueryStr}${isPending(fence.syncStatus) ? '&readOnlyPending=true' : ''}`;
    if (fence.type === 'POLYGON') {
      props.history.push(`${props.match.url}/polygon/${fence.id}/edit${editQueryStr}`);
    } else {
      props.history.push(`${props.match.url}/${fence.id}/edit${editQueryStr}`);
    }
  };

  const goToDelete = (fence: IVirtualFenceSummary) => {
    if (isDeleteBlocked(fence.syncStatus) || !fence.id) return;
    if (fence.type === 'POLYGON') {
      props.history.push(`${props.match.url}/polygon/${fence.id}/delete${pageQueryStr}`);
    } else {
      props.history.push(`${props.match.url}/${fence.id}/delete${pageQueryStr}`);
    }
  };

  const sort = (field: string) => () => {
    setPaginationState(prev => ({
      ...prev,
      order: prev.order === ASC ? DESC : ASC,
      sort: field,
    }));
  };

  const handlePagination = (_: unknown, page: number) =>
    setPaginationState(prev => ({ ...prev, activePage: page }));

  const totalCount = circleCount + polygonCount;

  const filterBorderColor = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.24)
    : theme.palette.divider;

  const selectedAllBg = theme.palette.mode === 'dark'
    ? theme.palette.grey[800]
    : theme.palette.grey[900];

  const tabButtonSx = {
    px: 1.5,
    py: 0.4,
    fontSize: 12,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    color: 'text.secondary',
    lineHeight: 1.4,
  };

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', padding: '24px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} sx={{ flexWrap: 'nowrap' }}>
        <Box display="flex" alignItems="center" overflow="hidden">
          <IconButton onClick={() => props.history.goBack()} aria-label="goBack" size="large" sx={{ color: '#808080' }}>
            <ArrowDownwardRounded sx={{ transform: 'rotate(90deg)' }} />
          </IconButton>
          <Typography
            id="virtual-fence-circle-heading"
            data-cy="VirtualFenceHeading"
            variant="h5"
            sx={{ fontSize: '1.5rem', color: '#808080', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            Cercas Virtuais
          </Typography>
        </Box>
        <Button
          color="secondary"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setChooserOpen(true)}
          data-cy="entityCreateButton"
          sx={{ borderRadius: '8px', textTransform: 'none', height: '42px', whiteSpace: 'nowrap', flexShrink: 0, ml: 1 }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Criar nova cerca virtual</Box>
        </Button>
      </Box>

      <VirtualFenceCreateChooser
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        baseUrl={props.match.url}
      />

      <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={typeFilter}
          exclusive
          onChange={(_, value: VirtualFenceTypeFilter | null) => {
            if (value) setTypeFilter(value);
          }}
          size="small"
          sx={{
            '& .MuiToggleButtonGroup-grouped': {
              border: `1px solid ${filterBorderColor} !important`,
              '&:not(:first-of-type)': {
                borderLeft: `1px solid ${filterBorderColor} !important`,
                marginLeft: '-1px',
              },
            },
            '& .MuiToggleButton-root': tabButtonSx,
            '& .MuiToggleButton-root.Mui-selected': {
              color: `${theme.palette.common.white} !important`,
            },
          }}
        >
          <ToggleButton
            value="ALL"
            sx={{
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: `${selectedAllBg} !important`,
                color: `${theme.palette.getContrastText(selectedAllBg)} !important`,
                borderColor: `${filterBorderColor} !important`,
              },
            }}
          >
            Todas ({totalCount})
          </ToggleButton>
          <ToggleButton
            value="CIRCLE"
            sx={{
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: `${theme.palette.info.main} !important`,
                borderColor: `${theme.palette.info.main} !important`,
              },
            }}
          >
            Circulares ({circleCount})
          </ToggleButton>
          <ToggleButton
            value="POLYGON"
            sx={{
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: `${theme.palette.success.main} !important`,
                borderColor: `${theme.palette.success.main} !important`,
              },
            }}
          >
            Poligonais ({polygonCount})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!loading && fenceList.length > 0 && (
        <TableContainer sx={{ marginTop: '16px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <DoNotShowInProduction>
                  <HeadTableCell>
                    <TableSortLabel
                      active={paginationState.sort === 'id'}
                      direction={paginationState.order as 'desc' | 'asc'}
                      onClick={sort('id')}
                      IconComponent={ArrowDownwardRounded}
                    >
                      <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', fontWeight: 600 }}>ID</Typography>
                    </TableSortLabel>
                  </HeadTableCell>
                </DoNotShowInProduction>
                <HeadTableCell>
                  <TableSortLabel
                    active={paginationState.sort === 'name'}
                    direction={paginationState.order as 'desc' | 'asc'}
                    onClick={sort('name')}
                    IconComponent={ArrowDownwardRounded}
                  >
                    <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', fontWeight: 600 }}>Nome</Typography>
                  </TableSortLabel>
                </HeadTableCell>
                <HeadTableCell>
                  <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', fontWeight: 600 }}>Descrição</Typography>
                </HeadTableCell>
                <HeadTableCell>
                  <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', fontWeight: 600 }}>Classificação</Typography>
                </HeadTableCell>
                <HeadTableCell>
                  <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', fontWeight: 600 }}>Tipo</Typography>
                </HeadTableCell>
                <HeadTableCell>
                  <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', fontWeight: 600 }}>Status</Typography>
                </HeadTableCell>
                {/* spacer for action buttons */}
                <HeadTableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {fenceList.map(fence => {
                const pending = isPending(fence.syncStatus);
                const deleteBlocked = isDeleteBlocked(fence.syncStatus);
                const editTooltip = pending ? 'Visualizar cerca' : 'Editar';
                return (
                  <TableRow
                    key={`${fence.type}-${fence.id}`}
                    data-cy="entityTable"
                  >
                    <DoNotShowInProduction>
                      <BodyTableCell>
                        <Typography color="primary.contrastText" fontSize="0.875rem">
                          {fence.id}
                        </Typography>
                      </BodyTableCell>
                    </DoNotShowInProduction>

                    <BodyTableCell>
                      <Typography color="primary.contrastText" fontSize="0.875rem" fontWeight={600}>
                        {fence.name || '-'}
                      </Typography>
                    </BodyTableCell>

                    <BodyTableCell>
                      <Typography color="primary.contrastText" fontSize="0.875rem">
                        {fence.description || '-'}
                      </Typography>
                    </BodyTableCell>

                    <BodyTableCell>
                      <ClassificationChip fence={fence} />
                    </BodyTableCell>

                    <BodyTableCell>
                      <TypeChip type={fence.type} />
                    </BodyTableCell>

                    <BodyTableCell>
                      <StatusChip fence={fence} />
                    </BodyTableCell>

                    <BodyTableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <Tooltip title={editTooltip}>
                          <span>
                            <IconButton
                              size="small"
                              data-cy="entityEditButton"
                              onClick={() => goToEdit(fence)}
                            >
                              <EditRounded color="secondary" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={deleteBlocked ? 'Exclusão já está pendente' : 'Excluir'}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={deleteBlocked}
                              data-cy="entityDeleteButton"
                              onClick={() => goToDelete(fence)}
                            >
                              <DeleteRounded color={deleteBlocked ? 'disabled' : 'error'} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </BodyTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && fenceList.length === 0 && <NoData title="Nenhuma cerca virtual encontrada" />}
      {loading && <Loading />}

      <PaginationComponent
        paginationState={paginationState}
        totalItems={totalItems}
        onChange={handlePagination}
        list={fenceList}
      />
    </Box>
  );
};

export default VirtualFenceList;

const HeadTableCell = styled(TableCell)(() => ({
  borderBottom: 'none',
  paddingBottom: 8,
}));

const BodyTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: '8px solid',
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  whiteSpace: 'nowrap',
}));
